import React, { useMemo } from "react";
import { Columns } from "components/table";
import { FleetRow } from "../types";
import { PlateNumberCell, ActionCell } from "../components/table-cells";
import { detailFleetPageRouteName } from "../../constants/routes";
import { useNavigate } from "react-router-dom";
import { dateTimeFormatter, dateFormatter } from "_helper/timeFormatter";

interface UseFleetTableColumnsProps {
  t: any;
  handleDeleteRequest: (row: FleetRow) => void;
  handleToggleStatus: (row: FleetRow, status: string) => void;
  handleEditRequest: (id: string) => void;
}

export const useFleetColumns = ({
  t,
  handleDeleteRequest,
  handleToggleStatus,
  handleEditRequest,
}: UseFleetTableColumnsProps) => {
  const navigate = useNavigate();
  return useMemo(
    (): Columns<FleetRow>[] => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
        sticky: "left",
      },
      {
        id: "plateNumber",
        fieldId: "plateNumber",
        label: t("col-plate-number"),
        width: "200px",
        sortable: true,
        render: (row: FleetRow) => <PlateNumberCell row={row} />,
      },
      {
        id: "vehicleType",
        fieldId: "vehicleType",
        label: t("col-vehicle-type"),
        sortable: true,
        render: (row: FleetRow) => row.vehicleType || "-",
      },
      {
        id: "vehicleModel",
        fieldId: "vehicleModel",
        label: t("col-vehicle-model"),
        sortable: true,
        render: (row: FleetRow) => row.vehicleModel || "-",
      },
      {
        id: "odometer",
        fieldId: "odometer",
        label: t("col-odometer"),
        sortable: true,
        render: (row: FleetRow) => 
          row.odometer !== undefined && row.odometer !== null
             ? `${Number(row.odometer).toLocaleString()} ${t("unit-km")}` 
            : "-",
      },

      {
        id: "stnkExpiry",
        fieldId: "stnkExpiry",
        label: t("col-stnk-expiry"),
        sortable: true,
        render: (row: FleetRow) => dateFormatter(row.stnkExpiry),
      },
      {
        id: "kirExpiry",
        fieldId: "kirExpiry",
        label: t("col-kir-expiry"),
        sortable: true,
        render: (row: FleetRow) => dateFormatter(row.kirExpiry),
      },
      {
        id: "lastMaintenance",
        fieldId: "lastMaintenance",
        label: t("col-last-maintenance"),
        sortable: true,
        render: (row: FleetRow) => dateFormatter(row.lastMaintenance),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        render: (row: FleetRow) => (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary">{row.createdBy || "-"}</span>
            <span className="text-xs text-greyScale-60">{row.createdAt || "-"}</span>
          </div>
        ),
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        render: (row: FleetRow) => (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary">{row.updatedBy || "-"}</span>
            <span className="text-xs text-greyScale-60">{row.updatedAt || "-"}</span>
          </div>
        ),
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "120px",
        dragDisabled: true,
        align: "center",
        sticky: "right",
        render: (row: FleetRow) => (
          <ActionCell 
            row={row} 
            t={t} 
            handleEditRequest={handleEditRequest}
            handleToggleStatus={handleToggleStatus} 
            handleDeleteRequest={handleDeleteRequest} 
          />
        ),
      },
    ],
    [t, handleEditRequest, handleToggleStatus, handleDeleteRequest]
  );
};
